/**
 * The job's Dropbox folder, in the report screen.
 *
 * Reviewing a legacy-process report used to mean two windows — the dashboard
 * for the report, Dropbox for the evidence it was built from — and a lot of
 * retyping between them. This shows the folder in place: browse it, preview
 * the checklist scan or a photo, and attach a file straight onto a clause or
 * a photo prompt without leaving the report.
 *
 * Loading is deliberate rather than automatic for field-tool reports: listing
 * costs a Dropbox search, and a PWA-captured job has nothing there worth the
 * call. Legacy imports open expanded, because for those the folder IS the
 * evidence.
 *
 * Retires with the Dropbox adapter (docs/ROADMAP.md).
 */

import { useCallback, useEffect, useState } from 'react';
import type { DropboxBrowseEntry, DropboxBrowseResponse, FieldReport } from '@cer-autofill/shared';
import {
  apiErrorReason,
  attachDropboxFile,
  fetchDropboxFileBlobUrl,
  fetchDropboxFolder,
} from '../api.js';
import { FilingPicker, filingTargetsFor } from './FilingPicker.js';
import { formatDateTime } from '../format.js';
import { openFileInNewTab } from '../lib/open-file.js';

/** Bytes → a short human size. Dropbox photos are MB-scale, scans KB-scale. */
function formatSize(bytes: number | undefined): string {
  if (bytes === undefined) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** The last path segment, for a breadcrumb that doesn't run off the card. */
function segments(jobFolderPath: string, path: string): string[] {
  if (path.toLowerCase() === jobFolderPath.toLowerCase()) return [];
  return path.slice(jobFolderPath.length).split('/').filter(Boolean);
}

interface PreviewState {
  path: string;
  name: string;
  url: string;
  contentType: string;
}

export function DropboxPanel({
  jobId,
  submission,
  autoLoad,
  onAttached,
}: {
  jobId: string;
  /** Drives the filing targets offered when attaching. */
  submission: FieldReport;
  /** Legacy imports open expanded; field-tool reports wait for a click. */
  autoLoad: boolean;
  /** Attaching writes to the photo store — the parent refetches. */
  onAttached: () => void;
}) {
  const [open, setOpen] = useState(autoLoad);
  const [data, setData] = useState<DropboxBrowseResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewState | null>(null);
  const [previewBusyPath, setPreviewBusyPath] = useState<string | null>(null);
  const [attachPath, setAttachPath] = useState<string | null>(null);
  const [attachBusy, setAttachBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const browse = useCallback(
    (path?: string) => {
      setLoading(true);
      setError(null);
      void fetchDropboxFolder(jobId, path)
        .then(setData)
        .catch((e: unknown) => setError(apiErrorReason(e)))
        .finally(() => setLoading(false));
    },
    [jobId],
  );

  useEffect(() => {
    if (open && data === null && !loading && error === null) browse();
    // Only re-runs when the panel opens; `browse` is stable per jobId.
  }, [open, data, loading, error, browse]);

  // Object URLs are ours to free — a review session opening a dozen previews
  // would otherwise hold every one of them until the tab closed.
  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview.url);
    };
  }, [preview]);

  function closePreview(): void {
    setPreview((current) => {
      if (current) URL.revokeObjectURL(current.url);
      return null;
    });
  }

  async function openPreview(entry: DropboxBrowseEntry): Promise<void> {
    setPreviewBusyPath(entry.path);
    setError(null);
    try {
      if (/\.pdf$/i.test(entry.name)) {
        await openFileInNewTab(async () => {
          const { url } = await fetchDropboxFileBlobUrl(jobId, entry.path);
          return url;
        });
        return;
      }
      const { url, contentType } = await fetchDropboxFileBlobUrl(jobId, entry.path);
      setPreview((current) => {
        if (current) URL.revokeObjectURL(current.url);
        return { path: entry.path, name: entry.name, url, contentType };
      });
    } catch (e) {
      setError(apiErrorReason(e));
    } finally {
      setPreviewBusyPath(null);
    }
  }

  async function attach(path: string, promptKey: string): Promise<void> {
    setAttachBusy(true);
    setError(null);
    try {
      const manifest = await attachDropboxFile(jobId, { path, promptKey });
      setAttachPath(null);
      setNotice(`Attached ${manifest.filename} as ${promptKey}.`);
      onAttached();
    } catch (e) {
      setError(apiErrorReason(e));
    } finally {
      setAttachBusy(false);
    }
  }

  if (!open) {
    return (
      <div className="card" id="report-dropbox">
        <div className="dropbox-panel__head">
          <h3>Dropbox folder</h3>
          <button type="button" className="ghost-btn" onClick={() => setOpen(true)}>
            Browse folder
          </button>
        </div>
        <p className="muted">Browse job files to preview or attach them to this report.</p>
      </div>
    );
  }

  const crumbs = data ? segments(data.jobFolderPath, data.path) : [];

  return (
    <div className="card" id="report-dropbox">
      <div className="dropbox-panel__head">
        <h3>Dropbox folder</h3>
        <button
          type="button"
          className="link-button"
          onClick={() => browse(data?.path)}
          disabled={loading}
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {error && <p className="error">{error}</p>}
      {notice && (
        <p className="banner banner--success">
          {notice}{' '}
          <button type="button" className="link-button" onClick={() => setNotice(null)}>
            Dismiss
          </button>
        </p>
      )}

      {data && (
        <div className="dropbox-panel__crumbs">
          <button
            type="button"
            className="link-button"
            onClick={() => browse()}
            disabled={crumbs.length === 0}
          >
            Job folder
          </button>
          {crumbs.map((seg) => (
            <span key={seg}>
              {' / '}
              {seg}
            </span>
          ))}
        </div>
      )}

      {loading && !data && <p className="loading">Reading the folder…</p>}
      {data && data.entries.length === 0 && <p className="muted">This folder is empty.</p>}

      {data && data.entries.length > 0 && (
        <ul className="dropbox-list">
          {data.parentPath !== undefined && (
            <li className="dropbox-list__row">
              <button
                type="button"
                className="dropbox-list__name link-button"
                onClick={() => browse(data.parentPath)}
              >
                ← Up one level
              </button>
            </li>
          )}
          {data.entries.map((entry) => (
            <li key={entry.path} className="dropbox-list__row">
              {entry.kind === 'folder' ? (
                <button
                  type="button"
                  className="dropbox-list__name link-button"
                  onClick={() => browse(entry.path)}
                >
                  {entry.name}/
                </button>
              ) : (
                <span className="dropbox-list__name">{entry.name}</span>
              )}
              <span className="dropbox-list__meta">
                {formatSize(entry.size)}
                {entry.modified ? ` · ${formatDateTime(entry.modified)}` : ''}
              </span>
              <span className="dropbox-list__actions">
                {entry.previewable && (
                  <button
                    type="button"
                    className="link-button"
                    disabled={previewBusyPath === entry.path}
                    onClick={() => void openPreview(entry)}
                  >
                    {previewBusyPath === entry.path
                      ? 'Opening…'
                      : /\.pdf$/i.test(entry.name)
                        ? 'Open PDF in new tab'
                        : 'Preview'}
                  </button>
                )}
                {entry.attachable && (
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => setAttachPath(attachPath === entry.path ? null : entry.path)}
                  >
                    {attachPath === entry.path ? 'Cancel' : 'Attach'}
                  </button>
                )}
              </span>
              {attachPath === entry.path && (
                <div className="dropbox-list__picker">
                  <FilingPicker
                    targets={filingTargetsFor(submission)}
                    busy={attachBusy}
                    onCancel={() => setAttachPath(null)}
                    onPick={(promptKey) => void attach(entry.path, promptKey)}
                  />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {preview && (
        <div className="dropbox-preview">
          <div className="dropbox-preview__head">
            <strong>{preview.name}</strong>
            <button type="button" className="link-button" onClick={closePreview}>
              Close preview
            </button>
          </div>
          {preview.contentType.startsWith('image/') ? (
            <img src={preview.url} alt={preview.name} className="dropbox-preview__image" />
          ) : (
            <a href={preview.url} target="_blank" rel="noopener noreferrer">
              Open {preview.name} in a new tab
            </a>
          )}
        </div>
      )}
    </div>
  );
}
